"use client";

import { useHovercardProviderContext } from "./EM5CXX6A.js";
import { useFocusable } from "./LVA2YJMS.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useBooleanEvent, useEvent, useIsMouseMoving, useMergeRefs } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/hovercard/hovercard-anchor.tsx
import { addGlobalEventListener } from "@ariakit/core/utils/events";
import { disabledFromProps, invariant } from "@ariakit/core/utils/misc";
import { useCallback, useEffect, useRef } from "react";
var TagName = "a";
var useHovercardAnchor = createHook(function useHovercardAnchor2(_a) {
  var _b = _a,
    store = _b.store,
    _b$showOnHover = _b.showOnHover,
    showOnHover = _b$showOnHover === void 0 ? true : _b$showOnHover,
    props = __objRest(_b, ["store", "showOnHover"]);
  var context = useHovercardProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "HovercardAnchor must receive a `store` prop or be wrapped in a HovercardProvider component.");
  var disabled = disabledFromProps(props);
  var showTimeoutRef = useRef(0);
  useEffect(function () {
    return function () {
      return window.clearTimeout(showTimeoutRef.current);
    };
  }, []);
  useEffect(function () {
    var onMouseLeave = function onMouseLeave(event) {
      if (!store) return;
      var _store$getState = store.getState(),
        anchorElement = _store$getState.anchorElement;
      if (!anchorElement) return;
      if (event.target !== anchorElement) return;
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = 0;
    };
    return addGlobalEventListener("mouseleave", onMouseLeave, true);
  }, [store]);
  var onMouseMoveProp = props.onMouseMove;
  var showOnHoverProp = useBooleanEvent(showOnHover);
  var isMouseMoving = useIsMouseMoving();
  var onMouseMove = useEvent(function (event) {
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (disabled) return;
    if (!store) return;
    if (event.defaultPrevented) return;
    if (showTimeoutRef.current) return;
    if (!isMouseMoving()) return;
    if (!showOnHoverProp(event)) return;
    var element = event.currentTarget;
    store.setAnchorElement(element);
    store.setDisclosureElement(element);
    var _store$getState2 = store.getState(),
      showTimeout = _store$getState2.showTimeout,
      timeout = _store$getState2.timeout;
    var showHovercard = function showHovercard() {
      showTimeoutRef.current = 0;
      if (!isMouseMoving()) return;
      store == null ? void 0 : store.setAnchorElement(element);
      store == null ? void 0 : store.show();
      queueMicrotask(function () {
        store == null ? void 0 : store.setDisclosureElement(element);
      });
    };
    var timeoutMs = showTimeout != null ? showTimeout : timeout;
    if (timeoutMs === 0) {
      showHovercard();
    } else {
      showTimeoutRef.current = window.setTimeout(showHovercard, timeoutMs);
    }
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (!store) return;
    window.clearTimeout(showTimeoutRef.current);
    showTimeoutRef.current = 0;
  });
  var ref = useCallback(function (element) {
    if (!store) return;
    var _store$getState3 = store.getState(),
      anchorElement = _store$getState3.anchorElement;
    if (anchorElement == null ? void 0 : anchorElement.isConnected) return;
    store.setAnchorElement(element);
  }, [store]);
  props = __spreadProps(__spreadValues({}, props), {
    ref: useMergeRefs(ref, props.ref),
    onMouseMove: onMouseMove,
    onClick: onClick
  });
  props = useFocusable(props);
  return props;
});
var HovercardAnchor = forwardRef(function HovercardAnchor2(props) {
  var htmlProps = useHovercardAnchor(props);
  return createElement(TagName, htmlProps);
});
export { useHovercardAnchor, HovercardAnchor };